import moment from 'moment';
import 'moment-timezone';

const FACILITY_TIME_ZONE_KEY = 'facility_timezone';

// Read half of THIN-7611's helpers, lifted for BOPS-397. "null"/"undefined" count as missing.
const UNUSABLE_TIME_ZONES = new Set(['', 'null', 'undefined']);

const isUsableTimeZone = (value: unknown): value is string =>
  typeof value === 'string' && !UNUSABLE_TIME_ZONES.has(value.trim());

/** The stored facility timezone, or '' when the key is absent or holds a junk value. */
export const readStoredFacilityTimeZone = (): string => {
  const stored = localStorage.getItem(FACILITY_TIME_ZONE_KEY);
  return isUsableTimeZone(stored) ? stored : '';
};

/**
 * THIN-7916: the facility-local "today" as a JS Date at local midnight of that calendar day.
 * The Daily Session Notes page must default its date of service to the day the therapist is
 * actually working in — NOT the browser/UTC day, which rolls to "tomorrow" in the evening for
 * US Central/Eastern facilities and dated evening notes (and their billing) a day ahead.
 * Falls back to the browser's today when no facility timezone is stored.
 */
export const getFacilityTodayYmd = (): string => {
  const facilityTimeZone = localStorage.getItem('facility_timezone') || '';
  return facilityTimeZone ? moment().tz(facilityTimeZone).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
};

export const getFacilityToday = (): Date =>
  // Rebuild as a local-midnight Date so downstream `moment(selectedDate).format('YYYY-MM-DD')`
  // (which reads the Date in browser-local time) yields the facility calendar day.
  moment(getFacilityTodayYmd(), 'YYYY-MM-DD').toDate();

export const convertDateToSelectedTimeZone = (date: any) => {
  const getFacilityTimeZone = localStorage.getItem('facility_timezone') || '';
  if (!getFacilityTimeZone) return date;
  return (typeof date === 'string' ? new Date(date) : date)?.toLocaleString('en-US', {
    timeZone: getFacilityTimeZone,
  });
};

export const getTimeZoneOffset = (timeZone: string) => {
  const date = new Date();
  const timeZoneFormat = new Intl.DateTimeFormat('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  });
  const [{ value: hours }, , { value: minutes }] = timeZoneFormat.formatToParts(date);
  const offsetString = `${hours}:${minutes}`;
  return offsetString;
};

export const convertDateToFacilityDate = (date: any) => {
  const getFacilityTimeZone = localStorage.getItem('facility_timezone') || '';
  if (!getFacilityTimeZone) {
    console.warn('No facility time zone found. Returning the original date.');
    return date;
  }

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj)) {
      console.error('Invalid date provided:', date);
      return date;
    }
    // @ts-ignore
    const formattedDate = moment.tz(dateObj, getFacilityTimeZone).format();
    return formattedDate;
  } catch (error) {
    console.error('Error converting date:', error);
    return date;
  }
};

export function formatDateWithTimezone(dateString: any) {
  const getFacilityTimeZone = localStorage.getItem('facility_timezone') || '';
  const originalDate = moment(dateString, 'ddd MMM DD YYYY HH:mm:ss [GMT]ZZ');

  const formattedDate = originalDate
    .clone()
    .tz(getFacilityTimeZone, true)
    .format('YYYY-MM-DDTHH:mm:ssZ');

  return formattedDate;
}

export function formatDateWithTimezoneWithZeroSecond(dateString: any) {
  const getFacilityTimeZone = localStorage.getItem('facility_timezone') || '';
  const originalDate = moment(dateString, 'ddd MMM DD YYYY HH:mm:ss [GMT]ZZ').second(0);

  const formattedDate = originalDate
    .clone()
    .tz(getFacilityTimeZone, true)
    .format('YYYY-MM-DDTHH:mm:ssZ');

  return formattedDate;
}

export const convertIntoIsoFormat = (formattedDate: any) => {
  if (typeof formattedDate === 'string') {
    return formattedDate.replace(/([+-]\d{2}:\d{2}|Z)$/, '.000Z');
  } else {
    // Handle the case where formattedDate is not a string
    console.error('Invalid date format:', formattedDate);
    return null; // or you can return a default value if you prefer
  }
};
export const toISOWithOffset = (dateString: any) => {
  // Create a new Date object
  const date = new Date(dateString);

  // Extract components
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  // Get the timezone offset in minutes
  const offset = date.getTimezoneOffset();
  const offsetSign = offset > 0 ? '-' : '+';
  const offsetHours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
  const offsetMinutes = String(Math.abs(offset) % 60).padStart(2, '0');

  // Format the offset
  const timezoneOffset = `${offsetSign}${offsetHours}:${offsetMinutes}`;

  // Construct the ISO format string
  const isoString = `${year}-${month}-${day}T00:00:00${timezoneOffset}`;

  return isoString;
};

export const parseDateWithoutTimezone = (dateString: string | null | undefined): Date | null => {
  if (!dateString) return null;
  const [year, month, date] = dateString.split('-').map(Number);
  return new Date(year, month - 1, date);
};
